import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Lock, Loader2, FileText, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
const BASE_URL = API_URL.replace('/api/v1', '').replace('/api', '');

export default function SharedDocumentViewer() {
  const { slug } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const fetchDocument = useCallback(async (providedPassword = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = {};
      if (providedPassword) {
        headers['x-document-password'] = providedPassword;
      }

      const response = await axios.get(`${API_URL}/shared-documents/view/${slug}`, { headers });
      
      if (response.data.requiresPassword) {
        setRequiresPassword(true);
        setDocument({ title: response.data.title, documentType: response.data.documentType });
      } else {
        setDocument(response.data);
        setRequiresPassword(false);
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      if (err.response?.status === 401) {
        setPasswordError('Invalid password');
      } else if (err.response?.status === 403) {
        setError('This document is currently unavailable');
      } else if (err.response?.status === 410) {
        setError('This document has expired');
      } else if (err.response?.status === 404) {
        setError('Document not found');
      } else {
        setError(err.response?.data?.error || 'Failed to load document');
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');
    fetchDocument(password);
  };

  const handleDownload = async () => {
    if (!document?.originalFile?.downloadUrl) return;
    
    try {
      // Fetch the file as blob with proper headers
      const response = await axios.get(`${API_URL}/shared-documents/download/${slug}`, {
        responseType: 'blob',
        headers: password ? { 'x-document-password': password } : {}
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.originalFile.originalName || 'download');
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Unable to Load Document
          </h1>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  // Password required state
  if (requiresPassword) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <Lock className="w-12 h-12 text-primary-600 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {document?.title || 'Protected Document'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              This document is password protected
            </p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
              {passwordError && (
                <p className="mt-2 text-sm text-red-500">{passwordError}</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              View Document
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Render as webpage - all pages displayed vertically
  return (
    <div className="min-h-screen bg-white">
      {/* Minimal Header */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-200 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{document?.title}</h1>
            {document?.description && (
              <p className="text-gray-500 text-sm mt-1">{document.description}</p>
            )}
          </div>
          
          {document?.viewSettings?.allowDownload && document?.originalFile && (
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>
          )}
        </div>
      </header>

      {/* Main Content - All pages rendered as webpage */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {document?.webView?.conversionStatus === 'processing' ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
              <p className="text-gray-600">Document is being processed...</p>
              <p className="text-gray-400 text-sm mt-2">Please refresh in a few moments</p>
            </div>
          </div>
        ) : document?.webView?.conversionStatus === 'failed' ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center max-w-md">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-gray-700 mb-2">Document preview is not available</p>
              <p className="text-gray-500 text-sm">{document.webView.conversionError}</p>
              {document?.viewSettings?.allowDownload && (
                <button
                  onClick={handleDownload}
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 mx-auto"
                >
                  <Download className="w-4 h-4" />
                  Download Original
                </button>
              )}
            </div>
          </div>
        ) : document?.documentType === 'html' ? (
          // HTML content - render in iframe for proper script execution
          <div className="w-full">
            {document?.webView?.pages?.map((page, idx) => (
              <iframe
                key={idx}
                srcDoc={page.htmlContent}
                className="w-full border-0 rounded-lg shadow-lg bg-white"
                style={{ minHeight: '80vh' }}
                title={`${document.title} - Page ${idx + 1}`}
                sandbox="allow-scripts allow-same-origin"
              />
            ))}
          </div>
        ) : (
          // PDF/PPT - render all pages as images vertically
          <div className="space-y-4">
            {document?.webView?.pages?.map((page, idx) => (
              <div key={idx} className="bg-white shadow-lg rounded-lg overflow-hidden">
                {page.imageUrl ? (
                  <img
                    src={`${BASE_URL}${page.imageUrl}`}
                    alt={`Page ${page.pageNumber || idx + 1}`}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                ) : page.htmlContent ? (
                  <div 
                    className="p-8"
                    dangerouslySetInnerHTML={{ __html: page.htmlContent }}
                  />
                ) : (
                  <div className="aspect-[4/3] flex items-center justify-center bg-gray-100 text-gray-400">
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-2" />
                      <p>Page {page.pageNumber || idx + 1}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-6 mt-8">
        <div className="max-w-5xl mx-auto px-4 text-center text-gray-400 text-sm">
          {document?.webView?.totalPages && (
            <p>{document.webView.totalPages} pages</p>
          )}
        </div>
      </footer>
    </div>
  );
}
